<script>
  import { mapGetters } from 'vuex';
  import { required } from 'vuelidate/lib/validators';
  import { ISSUE_PERMISSIONS, ISSUE_PRIORITY } from '@/_legacy/constants';
  import InputControl from '@/_legacy/components/Form/InputControl.vue';
  import AutosizeTextarea from '@/_legacy/components/Form/AutosizeTextarea.vue';
  import CheckboxGroup from '@/_legacy/components/CheckboxGroup.vue';
  import InputControlDate from '@/_legacy/components/Form/InputControlDate.vue';
  import InputError from '@/_legacy/components/Form/InputError.vue';
  import CloseButton from '@/_legacy/components/Modals/CloseButton.vue';
  import SelectControl from '@/_legacy/components/Controls/SelectControl.vue';
  import AddPhotos from '@/_legacy/components/Issues/AddPhotos.vue';
  import authMixin from '@/_legacy/mixins/authMixin';

  export default {
    name: 'ConductInspectionModalIssue',
    components: {
      SelectControl,
      CloseButton,
      InputError,
      InputControlDate,
      InputControl,
      AutosizeTextarea,
      CheckboxGroup,
      AddPhotos,
    },
    mixins: [authMixin],
    props: {
      item: {
        type: Object,
        required: true,
      },
      issues: {
        type: Object,
        default: () => ({}),
      },
    },
    data: () => ({
      isIssueExists: false,
      form: {
        title: '',
        description: '',
        assignees: [],
        priority: ISSUE_PRIORITY.MEDIUM,
        deadline: null,
        suggestedCorrectiveAction: '',
        photos: [],
      },
    }),
    validations: {
      form: {
        title: { required },
      },
    },
    computed: {
      ...mapGetters({
        assignees: 'assignees',
      }),
      priorityOptions() {
        return [
          {
            key: ISSUE_PRIORITY.MEDIUM,
            value: this.$t('Medium'),
            status: 'bg-yellow',
          },
          {
            key: ISSUE_PRIORITY.HIGH,
            value: this.$t('High'),
            status: 'bg-red',
          },
        ];
      },
      canCreateIssueWithAssignee() {
        return this.hasPermission(
          ISSUE_PERMISSIONS.CREATE_ISSUE_WITH_ASSIGNEE,
        );
      },
      issueForInspection() {
        return (
          this.issues
          && Object.keys(this.issues)
            .find(uuid => uuid === this.item.uuid)
        );
      },
      assigneesOptions() {
        return Object.keys(this.assignees)
          .map(id => ({
            value: id,
            label: this.assignees[id],
          }));
      },
    },
    mounted() {
      if (!this.issueForInspection) {
        return;
      }

      const {
        title,
        description,
        priority,
        deadline,
        // eslint-disable-next-line camelcase
        suggested_corrective_action,
        assignees,
        photos,
      } = this.issues[this.issueForInspection].params;

      this.form = {
        title,
        description,
        assignees: assignees.users || [],
        priority,
        deadline,
        suggestedCorrectiveAction: suggested_corrective_action,
        photos,
      };
    },
    methods: {
      handleClose() {
        this.$emit('close');
      },
      saveButtonClicked() {
        this.$v.$touch();

        if (this.$v.$invalid) {
          this.$toasted.show(
            this.$t('This page has form errors, please fix them'),
            { type: 'error' },
          );
        } else {
          this.$emit('save', this.form);
          this.handleClose();
        }
      },
      removeButtonClicked() {
        this.$emit('remove', this.form);
        this.handleClose();
      },
      assigneesChanged(assignees) {
        this.form.assignees = assignees;
      },
    },
  };
</script>

<template>
  <modal class="overflow-scroll" @modal-close="handleClose">
    <div class="modal-wrapper">
      <CloseButton @click.native="handleClose" />

      <div
        class="modal-content modal-content--scrollable"
        :class="{
                    'modal-content--editting': issueForInspection
                }"
      >
        <InputControl :label="$t('Title')">
          <input
            id="title"
            v-model.trim.lazy="$v.form.title.$model"
            class="form-input form-control form-input-bordered w-full"
            name="title"
            type="text"
            :placeholder="$t('Enter title')"
          >
          <div v-if="$v.form.title.$anyError">
            <InputError
              v-if="$v.form.title.$dirty"
              :condition="!$v.form.title.required"
            >
              {{
                $t('The {field} field is required', {
                  field: $t('title'),
                })
              }}
            </InputError>
          </div>
        </InputControl>

        <InputControl :label="$t('Description (optional)')">
          <AutosizeTextarea
            v-model.trim.lazy="form.description"
            :placeholder="$t('Enter description')"
          />
        </InputControl>

        <InputControl>
          <AddPhotos v-model="form.photos" />
        </InputControl>

        <InputControl
          v-if="canCreateIssueWithAssignee"
          :label="$t('Assignee (optional)')"
        >
          <SelectControl
            class="w-full"
            :multiple="true"
            :options="assigneesOptions"
            :value="form.assignees"
            :not-selected-label="$t('Select assignees')"
            @input="assigneesChanged(arguments[0])"
          />
        </InputControl>

        <InputControl :label="$t('Priority')">
          <CheckboxGroup
            v-model="form.priority"
            :options="priorityOptions"
          />
        </InputControl>

        <InputControl :label="$t('Due at (optional)')">
          <InputControlDate
            v-model="form.deadline"
            :class="{
                            empty: form.deadline === null
                        }"
            :placeholder="$t('Select deadline')"
            display-time
            wrap-size
          />
        </InputControl>

        <p class="mb-4">
          {{
            $t(
              'Click done to raise issue when submitting your check results',
            )
          }}
        </p>
      </div>

      <div class="modal-actions-outer-wrapper">
        <div class="modal-actions-inner-wrapper">
          <button
            v-if="issueForInspection"
            class="modal-actions-secondary modal-actions-secondary--transparent"
            type="button"
            @click="removeButtonClicked"
          >
            <span>{{ $t('Remove') }}</span>
          </button>
          <button
            class="modal-actions-primary"
            type="button"
            @click="saveButtonClicked"
          >
            <span>{{ $t('Done') }}</span>
          </button>
        </div>
      </div>
    </div>
  </modal>
</template>
